import { ApplicationConfig, provideZoneChangeDetection, SecurityContext, ErrorHandler } from '@angular/core';
import { provideRouter, withNavigationErrorHandler, NavigationError } from '@angular/router';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptors } from '@angular/common/http';
import {
  MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MSAL_GUARD_CONFIG,
  MsalInterceptor, MsalService, MsalBroadcastService, MsalGuard
} from '@azure/msal-angular';
import { PublicClientApplication, IPublicClientApplication, InteractionType } from '@azure/msal-browser';
import { provideMarkdown } from 'ngx-markdown';
import {
  LucideAngularModule, Type, CheckSquare, Edit2, PenTool, Brush, Eraser, PenLine,
  Image as ImageIcon, Table, LayoutGrid, MousePointer2, Wand2, FileText, Bold, Italic,
  Underline, Check, X, ArrowRight, ArrowLeft, List, Undo2, Redo, Download, Ban,
  UploadCloud, Plus, Save, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Folder, FolderOpen, BellPlus, Bookmark, Search, Clock, Moon, Sun, DollarSign, Database, HelpCircle, TrendingUp, Filter, Bot, Cpu, PieChart,
  ChevronDown, ChevronRight, Calendar, Share2, Sparkles, Brain, SlidersHorizontal,
  RefreshCw, Info, Zap, History, BarChart2, Building2
} from 'lucide-angular';
import { importProvidersFrom } from '@angular/core';

import { routes } from './app.routes';
import { AuthInterceptor } from './utils/auth.interceptor';
import { UiAccessRefreshInterceptor } from './utils/ui-access-refresh.interceptor';
import { GlobalErrorHandler, handleChunkLoadError } from './utils/global-error-handler';
import { AuthConfigService } from './auth-config.service';
import { environment } from '../environments/environment';
import { getLoginMode } from './utils/login-mode';

/**
 * Bootstrap MSAL instance for Angular DI.
 *
 * On first load (no auth code in URL): uses environment defaults as a placeholder.
 * On redirect back from B2C: reads the tenant-specific config that AuthConfigService
 * persisted to sessionStorage BEFORE the redirect. This ensures the correct clientId,
 * authority, and redirectUri are used to exchange the auth code — critical for
 * multi-tenant setups where CTE and Softpath have different B2C configurations.
 *
 * Super Admin (CIAM) redirects use a separate sessionStorage key (SUPERADMIN_CONFIG_KEY)
 * so they never repoint the tenant flow's config — see AuthConfigService. That means this
 * factory must check getLoginMode() to know which cached config to rebuild the instance
 * from after the redirect back; otherwise the bootstrap MSAL instance keeps the tenant/env
 * authority and silently can't process the CIAM auth response (no token calls at all).
 */
function msalInstanceFactory(): IPublicClientApplication {
  const currentOrigin = window.location.origin + '/';

  if (getLoginMode() === 'super-admin') {
    const superAdminConfig = AuthConfigService.getCachedSuperAdminConfig();
    if (superAdminConfig) {
      console.log('[msalInstanceFactory] Using cached Super Admin (CIAM) config — clientId:', superAdminConfig.clientId.substring(0, 8) + '...');
      return new PublicClientApplication({
        auth: {
          clientId: superAdminConfig.clientId,
          authority: superAdminConfig.authority,
          redirectUri: superAdminConfig.redirectUri,
          postLogoutRedirectUri: superAdminConfig.postLogoutRedirectUri,
          knownAuthorities: superAdminConfig.knownAuthorities,
        },
        cache: { cacheLocation: 'sessionStorage' },
        system: { loggerOptions: { loggerCallback: () => {} } },
      });
    }
  }

  // Check if we have a cached tenant config from the login flow
  const cachedConfig = AuthConfigService.getCachedConfig();

  if (cachedConfig) {
    console.log('[msalInstanceFactory] Using cached tenant config — clientId:', cachedConfig.clientId.substring(0, 8) + '...');
    return new PublicClientApplication({
      auth: {
        clientId: cachedConfig.clientId,
        authority: cachedConfig.authority,
        redirectUri: cachedConfig.redirectUri,
        postLogoutRedirectUri: cachedConfig.postLogoutRedirectUri,
        knownAuthorities: cachedConfig.knownAuthorities,
      },
      cache: { cacheLocation: 'sessionStorage' },
      system: { loggerOptions: { loggerCallback: () => {} } },
    });
  }

  // Fallback: no cached config — use environment defaults with dynamic origin
  const { clientId, authority, knownAuthorities } = environment.azureB2c;
  console.log('[msalInstanceFactory] No cached tenant config — using environment defaults');
  return new PublicClientApplication({
    auth: { clientId, authority, redirectUri: currentOrigin, postLogoutRedirectUri: currentOrigin, knownAuthorities },
    cache: { cacheLocation: 'sessionStorage' },
    system: { loggerOptions: { loggerCallback: () => {} } },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideRouter(
      routes,
      // Single place where a failed lazy-route chunk is handled: the router reports
      // the URL the user was actually navigating to, which is what we need to land
      // them on the right page after the recovery reload. If recovery declines
      // (attempts exhausted), the error keeps propagating to GlobalErrorHandler so
      // it is still logged/reported — navigation is never left hanging.
      withNavigationErrorHandler((navError: NavigationError) => {
        handleChunkLoadError(navError?.error ?? navError, navError?.url);
      })
    ),
    provideHttpClient(withInterceptors([AuthInterceptor, UiAccessRefreshInterceptor])),
    provideMarkdown({ sanitize: SecurityContext.HTML }),
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: environment.azureB2c.scopes },
      },
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map<string, Array<string>>(),
      },
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    importProvidersFrom(LucideAngularModule.pick({
      Type, CheckSquare, Edit2, PenTool, Brush, Eraser, PenLine, Image: ImageIcon, Table,
      LayoutGrid, MousePointer2, Wand2, FileText, Bold, Italic, Underline, Check, X,
      ArrowRight, ArrowLeft, List, Undo2, Redo, Download, Ban, UploadCloud, Plus, Save,
      AlignLeft, AlignCenter, AlignRight, AlignJustify, Folder, FolderOpen, BellPlus, Bookmark, Search, Clock, Moon, Sun, DollarSign, Database,
      HelpCircle, TrendingUp, Filter, Bot, Cpu, PieChart, ChevronDown, ChevronRight,
      Calendar, Share2, Sparkles, Brain, SlidersHorizontal, RefreshCw, Info, Zap, History, BarChart2, Building2
    }))
  ],
};

